import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { EASE } from "@/components/Reveal";

const NAV = [
  { label: "Home", path: "/", testId: "nav-link-home" },
  { label: "Vendita", path: "/vendita", testId: "nav-link-vendita" },
  { label: "Locazione", path: "/locazione", testId: "nav-link-locazione" },
  { label: "Il Gruppo", path: "/il-gruppo", testId: "nav-link-il-gruppo" },
  { label: "Contatti", path: "/contatti", testId: "nav-link-contatti" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const onDark = !scrolled && isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || !isHome ? "bg-white border-b border-ink/10 shadow-[0_1px_24px_rgba(24,28,32,0.06)]" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 lg:px-12">
          <Link to="/" data-testid="header-logo" aria-label="IDEAL REAL ESTATE — Home" className="relative z-10">
            <Logo theme={onDark ? "sand" : "ink"} />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `text-[12px] font-medium uppercase tracking-wide2 transition-colors duration-300 ${
                    onDark
                      ? isActive
                        ? "text-sand"
                        : "text-sand/70 hover:text-sand"
                      : isActive
                        ? "text-champagne"
                        : "text-ink hover:text-champagne"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contatti"
              data-testid="header-btn-sell-home"
              className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[12px] font-medium uppercase tracking-wide2 transition-all duration-300 ${
                onDark
                  ? "bg-sand text-ink hover:bg-champagne hover:text-sand"
                  : "bg-ink text-sand hover:bg-champagne"
              }`}
            >
              Vendi la tua casa
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="relative z-10 flex h-11 w-11 items-center justify-center lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
          >
            {open ? <X className="h-6 w-6 text-sand" /> : (
              <Menu className={`h-6 w-6 ${onDark ? "text-sand" : "text-ink"}`} />
            )}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-8"
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <nav className="flex flex-col gap-2">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={item.path}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `font-serif text-5xl leading-tight ${isActive ? "italic text-champagne" : "text-sand"}`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              className="absolute bottom-10 left-8 text-[11px] uppercase tracking-wide2 text-sand/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
            >
              Milano · Lombardia
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
